import { Component, EventEmitter, Input, Output, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FileUploader } from "ng2-file-upload";
import { StorageService, UserService, ExchangeService, BaseComponent, HelperService, WindowService } from "@mypxplat/xplat/core";
import { ImageCropperComponent, CropperSettings } from "ngx-img-cropper";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-upload-file",
  templateUrl: "upload-file.component.html",
  standalone: false,
})
export class UploadFileComponent extends BaseComponent implements OnInit {
  @Input() uploadMethod: any;
  @Input() allowedTypes: Array<string>;
  @Input() allowedFormatString: string = ".jpg, .png";
  @Input() cropRatio: number;
  @Input() editingUserId: any;
  @Output() fileUploaded: EventEmitter<any> = new EventEmitter();
  @ViewChild("cropper", { static: false }) cropper: ImageCropperComponent;
  public hasBaseDropZoneOver = false;
  public uploader: FileUploader;
  public uploadingComplete: boolean = false;
  public uploadingFile: any;
  public fileResponse: any;

  public isUploading: boolean = false;
  public uploadProgress: number = 0;
  /** Upload finished, but the modal is still waiting on the refreshed user before it can close. */
  public finalizing: boolean = false;

  public cropperSettings: CropperSettings;
  public data: any;
  public image: any;

  @Input() pluginImageUUID: string;

  constructor(
    private _win: WindowService,
    public storageService: StorageService,
    public userService: UserService,
    public activeModal: NgbActiveModal,
    public exchangeService: ExchangeService,
    public router: Router,
    public helperService: HelperService,
    public translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.cropperSettings = new CropperSettings();
    this.cropperSettings.noFileInput = true;
    this.cropperSettings.canvasWidth = 300;
    this.cropperSettings.canvasHeight = 200;
    this.cropperSettings.cropperClass = "imagecropper";
    if (this.uploadMethod == "change_profile_photo") {
      this.cropperSettings.croppedWidth = 300;
      this.cropperSettings.croppedHeight = 300;
    } else if (this.uploadMethod == "change_cover_photo") {
      this.cropperSettings.croppedWidth = 1000;
      this.cropperSettings.croppedHeight = 300;
      this.cropperSettings.width = 1000;
      this.cropperSettings.height = 300;
    } else if (this.uploadMethod == "add_learn_group_image") {
      this.cropperSettings.canvasWidth = 300;
      this.cropperSettings.canvasHeight = 169;
      this.cropperSettings.croppedWidth = 1000;
      this.cropperSettings.croppedHeight = 563;
      this.cropperSettings.width = 1000;
      this.cropperSettings.height = 563;
    }
    this.data = {};

    // FileUploader is kept purely as an off-the-shelf file picker + drag/drop
    // + queue helper. All HTTP now flows through UserService.uploadFile (paeapi
    // presigned URL flow), so `url`, `additionalParameter`, and `headers` are
    // unused. `disableMultipart` prevents ng2-file-upload from ever attempting
    // its own POST if `uploadAll()` gets called by accident.
    this.uploader = new FileUploader({
      isHTML5: true,
      method: "POST",
      queueLimit: 1,
      autoUpload: false,
      url: "unused://presigned-url-flow",
      disableMultipart: true,
      allowedMimeType: this.allowedTypes ? this.allowedTypes : null,
    });

    this.uploader.onAfterAddingFile = (f) => {
      this.uploadingFile = f;
      f.withCredentials = false;

      if (this.cropRatio) {
        // Load into the cropper; performUpload() will run when the user clicks the Upload button.
        // The source is normalized first so the cropper receives upright, EXIF-free pixels — see
        // normalizeImageSource() for why.
        const file: File = f._file;
        this.normalizeImageSource(file).then((src) => {
          this.image = new Image();
          this.image.onload = () => this.cropper.setImage(this.image);
          this.image.src = src;
        });
      } else if (this.uploadMethod == "add_plugin_image" && !this.pluginImageUUID) {
        alert("Please enter a UUID.");
        this.uploader.clearQueue();
      } else {
        // No cropping — legacy behaviour was to autoUpload here.
        this.performUpload();
      }
    };
  }

  /**
   * Returns a data URL for `file` whose pixels are already upright and which carries no EXIF block.
   *
   * Photos straight off a phone are stored in the sensor's native landscape orientation plus an EXIF
   * `Orientation` tag describing how to rotate them. Every browser we support honours that tag when it
   * decodes the image (`image-orientation: from-image` has been the default since Chrome 81 / Safari 13.1
   * / Firefox 77), so `naturalWidth`/`naturalHeight` and `drawImage()` are already correct. ngx-img-cropper
   * does not know that: its `getOrientedImage()` reads the tag itself and rotates a second time, so anything
   * tagged 3, 6 or 8 got cropped and uploaded sideways or upside down.
   *
   * Drawing through a canvas bakes the rotation in and drops the metadata, which leaves the cropper's
   * orientation pass nothing to find. It also keeps us inside iOS Safari's canvas limits — a 24MP photo
   * exceeds them, and the cropper's own PNG round-trip of one is enormous.
   */
  private normalizeImageSource(file: File): Promise<string> {
    return new Promise<string>((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const probe = new Image();

      // If anything goes wrong, fall back to the raw file so the upload still works. The worst case is
      // the pre-existing rotation bug, not a broken picker.
      const fallback = () => {
        URL.revokeObjectURL(objectUrl);
        const reader = new FileReader();
        reader.onloadend = (loadEvent: any) => resolve(loadEvent.target.result);
        reader.readAsDataURL(file);
      };

      probe.onerror = fallback;
      probe.onload = () => {
        URL.revokeObjectURL(objectUrl);
        try {
          const naturalWidth = probe.naturalWidth;
          const naturalHeight = probe.naturalHeight;
          if (!naturalWidth || !naturalHeight) return fallback();

          // iOS Safari rejects canvases over 4096px on a side or ~16MP in total.
          const MAX_EDGE = 4096;
          const MAX_AREA = 16777216;
          const scale = Math.min(1, MAX_EDGE / Math.max(naturalWidth, naturalHeight), Math.sqrt(MAX_AREA / (naturalWidth * naturalHeight)));
          const width = Math.max(1, Math.round(naturalWidth * scale));
          const height = Math.max(1, Math.round(naturalHeight * scale));

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) return fallback();
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(probe, 0, 0, width, height);

          // PNG keeps its alpha; everything else re-encodes as JPEG so the data URL stays manageable.
          const type = file.type === "image/png" ? "image/png" : "image/jpeg";
          resolve(canvas.toDataURL(type, 0.92));
        } catch (e) {
          fallback();
        }
      };

      probe.src = objectUrl;
    });
  }

  performUpload() {
    if (this.isUploading) return;

    // Compute the blob we're going to upload.
    let blob: Blob;
    let filename: string;
    let contentType: string;

    if (this.cropRatio && this.data && this.data.image) {
      // Data URL from ngx-img-cropper → Blob.
      const dataURL = this.data.image;
      const commaIdx = dataURL.indexOf(",");
      const meta = dataURL.substring(0, commaIdx);
      contentType = meta.split(":")[1].split(";")[0];
      const binary = atob(dataURL.substring(commaIdx + 1));
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      blob = new Blob([bytes], { type: contentType });
      const ext = contentType === "image/png" ? "png" : "jpg";
      const safeBase = this.uploadMethod === "change_cover_photo" ? "cover-photo" : "profile-photo";
      filename = `${safeBase}-${Date.now()}.${ext}`;
    } else if (this.uploader.queue.length && this.uploader.queue[0]._file) {
      const file: File = this.uploader.queue[0]._file;
      blob = file;
      filename = file.name;
      contentType = file.type || "application/octet-stream";
    } else {
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    const uuid = this.uploadMethod === "add_plugin_image" ? this.pluginImageUUID : undefined;
    this.userService.uploadFile(this.uploadMethod, blob, { fileName: filename, contentType, uuid }).subscribe({
      next: (event) => {
        this.uploadProgress = event.progress;
        if (event.done) {
          this.uploadingComplete = true;
          this.isUploading = false;
          this.fileResponse = event.response;
          this.fileUploaded.next(this.fileResponse);
          if (this.uploadMethod == "change_profile_photo" || this.uploadMethod == "change_cover_photo") {
            // The bytes are up, but the modal stays open until the refreshed user comes back. Without
            // this flag the cropper and its "Upload Now" button reappear for the duration, which reads
            // as though the upload never happened — hold the progress UI up instead.
            this.finalizing = true;
            this.userService.getUserDetails(false).subscribe({
              next: (result) => {
                if (this.userService.user.photoURL) {
                  const newProfilePhoto = this.userService.user.photoURL + "";
                  this.userService.user.photoURL = undefined;
                  this._win.setTimeout(() => {
                    this.userService.user.photoURL = newProfilePhoto;
                  }, 500);
                }
                this.finalizing = false;
                this.activeModal.close(result);
              },
              error: () => {
                // The file uploaded either way, so close rather than stranding the user on a spinner.
                this.finalizing = false;
                this.activeModal.close();
              },
            });
          }
        }
      },
      error: () => {
        this.isUploading = false;
        this.uploadingComplete = false;
        this.fileResponse = { error: this.translate.instant("sphere.upload.failed") };
        this.fileUploaded.next(this.fileResponse);
      },
    });
  }

  fileOverBase(e) {
    this.hasBaseDropZoneOver = e;
  }
}
